import { watch } from 'vue';
import { siteName } from './session';
import type { Router } from 'vue-router';

/**
 * What the browser tab says.
 *
 * `index.html` ships a static `<title>AWS Dashboard</title>` and **nothing ever
 * replaced it**. The deployment's own name was already known — it arrives on
 * the session bootstrap and the shell header has been rendering it all along —
 * so a client who named their dashboard saw that name in the page and the
 * generic one in every tab, bookmark and browser-history entry.
 *
 * Two inputs that change independently: the route, and the name arriving after
 * the first `/api/me`. Both are watched, because whichever lands second has to
 * win and a one-shot at startup would lose the name every time.
 */

/** The separator is a middot rather than a dash: a dash is in too many names. */
export function titleFor(page: string | undefined, site: string): string {
	const trimmed = (page ?? '').trim();
	if (site.trim() === '') return trimmed;
	return trimmed === '' ? site : `${trimmed} · ${site}`;
}

export function installDocumentTitle(router: Router): void {
	const apply = (): void => {
		document.title = titleFor(router.currentRoute.value.meta.title, siteName.value);
	};

	router.afterEach(apply);

	/**
	 * The name is not known at startup — it comes back with `/api/me`, after
	 * the first navigation has already been and gone. Without this the tab
	 * would keep whatever it had until the user navigated again.
	 */
	watch(siteName, apply);

	apply();
}
